import React, { forwardRef } from "react";
import { imageUrl, rupee } from "../lib/api";
import { formatInvoiceDate, storeBillingAddress } from "../lib/saleInvoice";
import {
  amountInWordsIndian,
  refundMethodLabel,
  returnGstSummaryLines,
  returnLineItems,
  returnReasonText,
} from "../lib/saleReturnDocument";

function MetaField({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <tr>
      <td className="gi-info-label">{label}</td>
      <td className="gi-info-value">{value}</td>
    </tr>
  );
}

function InfoPairRow({ label1, value1, label2, value2 }) {
  const showLeft = value1 !== undefined && value1 !== null && value1 !== "";
  const showRight = value2 !== undefined && value2 !== null && value2 !== "";
  if (!showLeft && !showRight) return null;
  return (
    <tr>
      <td className="gi-info-label">{showLeft ? label1 : ""}</td>
      <td className="gi-info-value">{showLeft ? value1 : ""}</td>
      <td className="gi-info-label gi-info-split">{showRight ? label2 : ""}</td>
      <td className="gi-info-value">{showRight ? value2 : ""}</td>
    </tr>
  );
}

function PartyBlock({ snap, gstin }) {
  const name = snap?.name || "Walk-in";
  const addressLine = [snap?.address, snap?.city, snap?.state].filter(Boolean).join(", ");
  return (
    <table className="gi-info-table gi-info-party">
      <tbody>
        <MetaField label="Name" value={name} />
        {snap?.customer_name && snap.customer_name !== name && (
          <MetaField label="Customer" value={snap.customer_name} />
        )}
        <MetaField label="GSTIN" value={gstin ?? snap?.gstin} />
        <MetaField label="Mobile" value={snap?.phone} />
        <MetaField label="Address" value={addressLine || undefined} />
      </tbody>
    </table>
  );
}

/**
 * GST Credit Note / Sales Return — printable document (Section 34, CGST Act).
 * Issued by supplier when goods sold are returned by the buyer.
 */
const SaleReturnView = forwardRef(function SaleReturnView({ sale, saleReturn, store }, ref) {
  if (!sale || !saleReturn) return null;

  const priceIncludesGst = store?.product_price_includes_gst !== false;
  const billing = storeBillingAddress(store);
  const storeState = store?.state || "";
  const customerState = sale.customer_snapshot?.state || "";
  const lines = returnLineItems(saleReturn, priceIncludesGst, storeState, customerState);
  const interState = (saleReturn.igst_amount || 0) > 0.009;
  const placeOfSupply = customerState || storeState || "—";
  const companyAddress = billing
    ? [billing.address, [billing.city, billing.state, billing.pincode].filter(Boolean).join(", ")].filter(Boolean).join(", ")
    : "";
  const gstLines = returnGstSummaryLines(saleReturn);
  const reason = returnReasonText(saleReturn);

  return (
    <div className="gst-invoice print-invoice" id="sales-return-credit-note" ref={ref}>
      <div className="flex items-center justify-between px-2 py-1 gi-border-b text-[11px]">
        <span>Page No. 1 of 1</span>
        <span className="font-bold text-sm tracking-wide">CREDIT NOTE</span>
        <span>Original for Recipient</span>
      </div>

      <div className="text-center text-[10px] py-1 gi-border-b font-medium tracking-wide uppercase">
        Sales Return · Document issued under Section 34 of the CGST Act, 2017
      </div>

      <div className="flex gi-border-b">
        <div className="w-24 shrink-0 gi-border-r p-2 flex items-center justify-center">
          {store?.image ? (
            <img src={imageUrl(store.image)} alt="" className="w-16 h-16 object-contain" />
          ) : (
            <div className="w-16 h-16 border border-dashed border-gray-400 grid place-items-center text-[10px] text-gray-500 text-center px-1">
              Logo
            </div>
          )}
        </div>
        <div className="flex-1 p-3 text-center min-w-0">
          <div className="gi-company-name">{store?.name || "Store"}</div>
          {companyAddress && <div className="mt-1">{companyAddress}</div>}
          <div className="mt-1 flex flex-wrap justify-center gap-x-4 gap-y-0.5">
            {store?.phone && <span>Mobile : {store.phone}</span>}
            {store?.email && <span>Email : {store.email}</span>}
          </div>
          {store?.gst_number && <div className="mt-0.5">GSTIN of Supplier : {store.gst_number}</div>}
        </div>
      </div>

      <table className="gi-info-table gi-border-b">
        <colgroup>
          <col className="gi-col-label" />
          <col className="gi-col-value" />
          <col className="gi-col-label" />
          <col className="gi-col-value" />
        </colgroup>
        <tbody>
          <InfoPairRow
            label1="Credit Note No."
            value1={saleReturn.return_no}
            label2="Credit Note Date"
            value2={formatInvoiceDate(saleReturn.created_at)}
          />
          <InfoPairRow
            label1="Original Invoice No."
            value1={saleReturn.invoice_no || sale.invoice_no}
            label2="Original Invoice Date"
            value2={formatInvoiceDate(sale.created_at)}
          />
          <InfoPairRow
            label1="Place of Supply"
            value1={placeOfSupply}
            label2="Supply Type"
            value2={interState ? "Inter-state" : "Intra-state"}
          />
          <InfoPairRow
            label1="GST Type"
            value1={interState ? "IGST" : "CGST + SGST"}
            label2="Reverse Charge"
            value2="N"
          />
          <InfoPairRow
            label1="Nature of Supply"
            value1="Goods (Return)"
            label2="Refund / Adjustment"
            value2={refundMethodLabel(saleReturn.refund_method)}
          />
          <tr>
            <td className="gi-info-label">Reason for Issuance</td>
            <td className="gi-info-value" colSpan={3}>{reason}</td>
          </tr>
        </tbody>
      </table>

      <div className="gi-split-2 gi-border-b">
        <div>
          <div className="gi-party-title">Details of Recipient (Buyer)</div>
          <PartyBlock snap={sale.customer_snapshot} gstin={sale.customer_snapshot?.gstin} />
        </div>
        <div>
          <div className="gi-party-title">Details of Supplier (Seller)</div>
          <table className="gi-info-table gi-info-party">
            <tbody>
              <MetaField label="Name" value={store?.name} />
              <MetaField label="GSTIN" value={store?.gst_number} />
              <MetaField label="Mobile" value={store?.phone} />
              <MetaField label="Address" value={companyAddress || undefined} />
            </tbody>
          </table>
        </div>
      </div>

      <div className="gi-scroll">
        <table className="gi-items-table">
          <thead>
            <tr>
              <th className="gi-th-center" style={{ width: "2rem" }}>Sr.</th>
              <th className="gi-th-left">Description of Goods Returned</th>
              <th className="gi-th-center" style={{ width: "4.5rem" }}>HSN/SAC</th>
              <th className="gi-th-center" style={{ width: "2.5rem" }}>Qty</th>
              <th className="gi-th-center" style={{ width: "2.5rem" }}>Unit</th>
              <th className="gi-th-num" style={{ width: "4.5rem" }}>Rate (₹)</th>
              <th className="gi-th-num" style={{ width: "4.5rem" }}>Taxable (₹)</th>
              <th className="gi-th-center" style={{ width: "2.5rem" }}>Tax %</th>
              {interState ? (
                <th className="gi-th-num" style={{ width: "4rem" }}>IGST (₹)</th>
              ) : (
                <>
                  <th className="gi-th-num" style={{ width: "3.5rem" }}>CGST (₹)</th>
                  <th className="gi-th-num" style={{ width: "3.5rem" }}>SGST (₹)</th>
                </>
              )}
              <th className="gi-th-num" style={{ width: "5rem" }}>Total (₹)</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.sr}>
                <td className="gi-center">{line.sr}</td>
                <td>
                  <div>{line.name}</div>
                  {line.barcode && <div className="text-[10px] text-gray-600 font-mono">{line.barcode}</div>}
                </td>
                <td className="gi-center font-mono">{line.hsn_code || "—"}</td>
                <td className="gi-center">{line.qty}</td>
                <td className="gi-center">Pcs</td>
                <td className="gi-num">{rupee(line.unitPriceBeforeTax)}</td>
                <td className="gi-num">{rupee(line.taxable)}</td>
                <td className="gi-center">{line.rate}%</td>
                {interState ? (
                  <td className="gi-num">{rupee(line.igst)}</td>
                ) : (
                  <>
                    <td className="gi-num">{rupee(line.cgst)}</td>
                    <td className="gi-num">{rupee(line.sgst)}</td>
                  </>
                )}
                <td className="gi-num font-semibold">{rupee(line.lineTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="gi-split-2 gi-border-t">
        <div className="gi-notes-panel min-h-[6rem]">
          <div className="font-semibold mb-1">Declaration</div>
          <p className="text-[11px] leading-snug">
            We hereby certify that the above particulars are true and correct. This credit note is issued
            for reduction in tax liability on account of return of goods previously supplied vide tax invoice
            <strong> {saleReturn.invoice_no || sale.invoice_no}</strong> dated <strong>{formatInvoiceDate(sale.created_at)}</strong>.
            The credit amount may be adjusted against future supplies or refunded as indicated above.
          </p>
          <p className="mt-2 text-[11px] font-medium">
            Amount in words: {amountInWordsIndian(saleReturn.total)}
          </p>
          {saleReturn.created_by_name && (
            <p className="mt-2 text-[11px]" style={{ color: "#444" }}>
              Prepared by: {saleReturn.created_by_name}
            </p>
          )}
        </div>
        <div className="gi-summary">
          <div className="gi-summary-row">
            <span className="gi-summary-label">Taxable Value (Credit)</span>
            <span className="gi-summary-value">{rupee(saleReturn.subtotal)}</span>
          </div>
          {gstLines.map((line) => (
            <div key={line.label} className="gi-summary-row">
              <span className="gi-summary-label">{line.label}</span>
              <span className="gi-summary-value">{rupee(line.amount)}</span>
            </div>
          ))}
          {gstLines.length > 1 && (
            <div className="gi-summary-row">
              <span className="gi-summary-label">Total GST (Credit)</span>
              <span className="gi-summary-value">{rupee(saleReturn.tax_amount)}</span>
            </div>
          )}
          <div className="gi-summary-row gi-summary-total">
            <span className="gi-summary-label">Total Credit Amount (Incl. GST)</span>
            <span className="gi-summary-value">{rupee(saleReturn.total)}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-0 gi-border-t text-[11px]">
        <div className="p-4 gi-border-r min-h-[4.5rem]">
          <div className="font-semibold mb-6">Customer / Recipient</div>
          <div className="border-t border-gray-400 pt-1 text-gray-600">Signature</div>
        </div>
        <div className="p-4 min-h-[4.5rem]">
          <div className="font-semibold mb-6">For {store?.name || "Store"}</div>
          <div className="border-t border-gray-400 pt-1 text-gray-600">Authorised Signatory</div>
        </div>
      </div>

      <div className="text-center text-[9px] py-1 gi-border-t text-gray-600">
        This is a computer-generated credit note. Registered person must report this credit note in GSTR-1 / GSTR-3B as applicable.
      </div>
    </div>
  );
});

export default SaleReturnView;
